/**
 * Game - Main Game Class
 *
 * Orchestrates the entire game loop, initializes all systems,
 * and manages game state.
 */

import { Vector2 } from './math/Vector2';
import { Vector3 } from './math/Vector3';
import { Renderer } from './core/Renderer';
import { InputManager, MOUSE_LEFT } from './core/InputManager';
import { Camera } from './core/Camera';
import { World } from './world/World';
import { Player } from './entities/Player';
import { InventoryComponent } from './items/InventoryComponent';
import { ItemRegistry } from './items/ItemRegistry';
import { UIManager } from './ui/UIManager';
import { InventoryPanel } from './ui/InventoryPanel';
import { StoragePanel } from './ui/StoragePanel';
import { CraftingSystem } from './crafting/CraftingSystem';
import { CraftingPanel } from './crafting/CraftingPanel';
import { BuildingSystem } from './building/BuildingSystem';
import { Structure } from './building/Structure';
import { BuildPanel } from './building/BuildPanel';
import { StructureType } from './types/enums';
import { EnemySpawner } from './combat/EnemySpawner';
import { Enemy } from './combat/Enemy';
import { WorldManager } from './world/WorldManager';
import { TimeManager } from './systems/TimeManager';
import { EventBus } from './systems/EventBus';
import { SaveManager, SaveData, WorldSaveData } from './save/SaveManager';
import { AudioManager } from './audio/AudioManager';
import { TileType, ResourceType } from './types/enums';
import { ToolItem, ConsumableItem, TorchItem, WeaponItem, ArmorItem, RangedWeaponItem } from './items/Item';
import { ProjectileManager, Projectile } from './combat/Projectile';
import { EquipmentComponent } from './items/EquipmentComponent';
import { EquipmentPanel } from './ui/EquipmentPanel';
import { MainMenuPanel } from './ui/MainMenuPanel';
import { SaveLoadPanel } from './ui/SaveLoadPanel';
import { PauseMenuPanel } from './ui/PauseMenuPanel';
import { CampfirePanel } from './ui/CampfirePanel';
import { DroppedItem } from './entities/DroppedItem';
import { LootTable } from './combat/LootTable';
import { t, Localization } from './systems/Localization';
import { StructureHoverInfo } from './ui/HUD';
import { LevelingSystem } from './systems/LevelingSystem';
import { Cursor } from './ui/Cursor';

export class Game {
  // Canvas and rendering
  private canvas: HTMLCanvasElement;
  private renderer: Renderer;
  private input: InputManager;
  private camera: Camera;
  private cursor: Cursor;

  // Game systems
  private world: World;
  private player: Player;
  private playerInventory: InventoryComponent;
  private playerEquipment: EquipmentComponent;
  private uiManager: UIManager;
  private craftingSystem: CraftingSystem;
  private buildingSystem: BuildingSystem;
  private enemySpawner: EnemySpawner;
  private timeManager: TimeManager;
  private saveManager: SaveManager;
  private levelingSystem: LevelingSystem;
  private projectileManager: ProjectileManager;
  private droppedItems: DroppedItem[] = [];

  // Ranged weapon state
  private lastFireTime: number = 0;

  // Timing
  private lastTimestamp: number = 0;
  private deltaTime: number = 0;
  private isRunning: boolean = false;
  private isPaused: boolean = false;
  private isGameOver: boolean = false;
  private inMainMenu: boolean = true;

  // Frame tracking
  private frameCount: number = 0;
  private fps: number = 0;
  private fpsUpdateTime: number = 0;

  // Audio initialized flag
  private audioInitialized: boolean = false;

  // Currently hovered enemy for visual feedback
  private hoveredEnemy: Enemy | null = null;

  // Currently hovered interactive structure for visual feedback
  private hoveredStructure: Structure | null = null;

  // World transition state
  private worldManager: WorldManager;
  private transitionState: {
    active: boolean;
    phase: 'falling' | 'black' | 'rising';
    progress: number;  // 0-1
    targetWorld: string;
  } | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;

    // Initialize core systems
    this.renderer = new Renderer(canvas);
    this.input = new InputManager(canvas);
    this.camera = new Camera();
    this.cursor = new Cursor(canvas);

    // Initialize world manager (handles multiple worlds)
    this.worldManager = new WorldManager(Date.now());
    const overworldData = this.worldManager.initializeOverworld();
    this.world = overworldData.world;
    this.buildingSystem = overworldData.buildingSystem;
    this.enemySpawner = overworldData.enemySpawner as EnemySpawner;

    // Find a valid spawn point
    const spawnPos = this.findSpawnPoint();

    // Initialize crafting (before UI so panels can use it)
    this.craftingSystem = new CraftingSystem();

    // Initialize player
    this.player = new Player(spawnPos);
    this.player.movement.canMoveTo = (x, y) => this.canPlayerMoveTo(x, y);
    this.world.addEntity(this.player);

    // Setup player inventory
    this.playerInventory = new InventoryComponent(20);
    this.player.addComponent(this.playerInventory);
    this.player.setInventoryRef(this.playerInventory);

    // Setup player equipment
    this.playerEquipment = new EquipmentComponent();

    // Give starting items
    this.giveStartingItems();

    // Initialize save (before UI so SaveLoadPanel can use it)
    this.saveManager = new SaveManager();
    this.saveManager.setAutoSave(true, 180); // 3 minute autosave

    // Initialize leveling system
    this.levelingSystem = new LevelingSystem();

    // Initialize projectile manager
    this.projectileManager = new ProjectileManager();
    this.levelingSystem.onLevelUp = (level, reward) => {
      this.uiManager.showSuccessToast(`Level ${level}! ${reward.description}`);
      console.log(`Leveled up to ${level}!`, reward);
    };
    this.levelingSystem.onXPGained = (amount, _total) => {
      this.uiManager.showInfoToast(`+${amount} XP`);
    };

    // Initialize UI
    this.uiManager = new UIManager();
    this.setupUI();

    // Setup autosave notification
    this.saveManager.onAutoSave = () => {
      this.uiManager.showInfoToast(t('notify_auto_saved'));
    };

    // Setup enemy spawner (from world manager)
    this.enemySpawner.setPlayer(this.player);
    this.enemySpawner.enemyCanMoveTo = (x, y) => this.canEnemyMoveTo(x, y);

    // Initialize time
    this.timeManager = new TimeManager(300); // 5 min days for faster testing
    this.setupTimeEvents();

    // Camera follows player
    this.camera.follow(this.player);

    // Setup event listeners
    this.setupEventListeners();

    console.log('Game initialized!');
  }

  private giveStartingItems(): void {
    const wood = ItemRegistry.get('wood');
    const stone = ItemRegistry.get('stone');
    const apple = ItemRegistry.get('apple');

    if (wood) this.playerInventory.addItem(wood, 10);
    if (stone) this.playerInventory.addItem(stone, 5);
    if (apple) this.playerInventory.addItem(apple, 3);
  }

  private setupUI(): void {
    // Inventory panel
    const inventoryPanel = new InventoryPanel();
    inventoryPanel.setInventory(this.playerInventory);
    inventoryPanel.centerOnScreen(this.canvas.width, this.canvas.height);
    // Set up callback for dropping items outside the panel
    inventoryPanel.onItemDroppedOutside = (itemStack) => {
      // If it's armor, try to equip it
      if (itemStack.item instanceof ArmorItem) {
        const previousItem = this.playerEquipment.equip(itemStack.item);
        if (previousItem) {
          // Add the previous item back to inventory
          this.playerInventory.addItem(previousItem, 1);
        }
        console.log(`Equipped ${itemStack.item.name}`);
      } else {
        // Otherwise delete the item
        console.log(`Deleted ${itemStack.quantity}x ${itemStack.item.name}`);
      }
    };
    // Double-click to equip armor from inventory
    inventoryPanel.onEquipItem = (item, slotIndex) => {
      const previousItem = this.playerEquipment.equip(item);
      // Remove the equipped item from inventory
      this.playerInventory.removeItemAtSlot(slotIndex, 1);
      if (previousItem) {
        // Add the previous item back to inventory
        this.playerInventory.addItem(previousItem, 1);
      }
      console.log(`Equipped ${item.name}`);
    };
    this.uiManager.registerPanel('inventory', inventoryPanel);

    // Equipment panel (positioned to the right of inventory)
    const equipmentPanel = new EquipmentPanel();
    equipmentPanel.setEquipment(this.playerEquipment);
    equipmentPanel.setInventory(this.playerInventory);
    // Position to the right of inventory
    const invBounds = inventoryPanel.getBounds();
    equipmentPanel.setPosition(invBounds.x + invBounds.width + 10, invBounds.y);
    equipmentPanel.onUnequipItem = (item) => {
      this.playerInventory.addItem(item, 1);
      console.log(`Unequipped ${item.name}`);
    };
    this.uiManager.registerPanel('equipment', equipmentPanel);

    // Crafting panel
    const craftingPanel = new CraftingPanel(this.craftingSystem);
    craftingPanel.setInventory(this.playerInventory);
    craftingPanel.centerOnScreen(this.canvas.width, this.canvas.height);
    this.uiManager.registerPanel('crafting', craftingPanel);

    // Build panel
    const buildPanel = new BuildPanel(this.buildingSystem);
    buildPanel.setInventory(this.playerInventory);
    buildPanel.centerOnScreen(this.canvas.width, this.canvas.height);
    this.uiManager.registerPanel('build', buildPanel);

    // Storage panel (for chests)
    const storagePanel = new StoragePanel();
    storagePanel.setPlayerInventory(this.playerInventory);
    storagePanel.centerOnScreen(this.canvas.width, this.canvas.height);
    this.uiManager.registerPanel('storage', storagePanel);

    // Main menu panel
    const mainMenuPanel = new MainMenuPanel();
    mainMenuPanel.centerOnScreen(this.canvas.width, this.canvas.height);
    mainMenuPanel.onContinue = () => this.continueGame();
    mainMenuPanel.onNewGame = () => this.startNewGame();
    mainMenuPanel.onLoadGame = () => this.showLoadMenu();
    this.uiManager.registerPanel('mainmenu', mainMenuPanel);

    // Save/Load panel
    const saveLoadPanel = new SaveLoadPanel(this.saveManager);
    saveLoadPanel.centerOnScreen(this.canvas.width, this.canvas.height);
    saveLoadPanel.onSave = (slot, name) => this.saveToSlot(slot, name);
    saveLoadPanel.onLoad = (slot) => this.loadFromSlot(slot);
    saveLoadPanel.onDelete = (slot) => this.deleteSave(slot);
    saveLoadPanel.onNewGame = () => this.startNewGame();
    this.uiManager.registerPanel('saveload', saveLoadPanel);

    // Pause menu panel
    const pauseMenuPanel = new PauseMenuPanel();
    pauseMenuPanel.centerOnScreen(this.canvas.width, this.canvas.height);
    pauseMenuPanel.onResume = () => this.resumeGame();
    pauseMenuPanel.onSave = () => this.quickSave();
    pauseMenuPanel.onSaveAs = () => this.showSaveMenu();
    pauseMenuPanel.onLoad = () => this.showLoadMenuFromPause();
    pauseMenuPanel.onNewGame = () => this.startNewGame();
    this.uiManager.registerPanel('pausemenu', pauseMenuPanel);

    // Campfire panel
    const campfirePanel = new CampfirePanel();
    campfirePanel.setInventory(this.playerInventory);
    campfirePanel.centerOnScreen(this.canvas.width, this.canvas.height);
    this.uiManager.registerPanel('campfire', campfirePanel);

    // Setup HUD
    this.uiManager.getHUD().setPlayer(this.player);
    this.uiManager.getHUD().setInventory(this.playerInventory);
  }

  private setupTimeEvents(): void {
    this.timeManager.onDayStart = (day) => {
      console.log(`Day ${day} started`);
      this.enemySpawner.setNight(false);
      EventBus.publish({ type: 'DAY_STARTED', day });
    };

    this.timeManager.onNightStart = (day) => {
      console.log(`Night ${day} started`);
      this.enemySpawner.setNight(true);
      EventBus.publish({ type: 'NIGHT_STARTED', day });
    };
  }

  private setupEventListeners(): void {
    // Subscribe to game events
    EventBus.subscribe('PLAYER_DIED', () => {
      console.log('Game Over!');
      this.isGameOver = true;
    });

    EventBus.subscribe('ENEMY_KILLED', (event) => {
      console.log(`Killed ${event.enemyType}, gained ${event.experienceAwarded} XP`);

      // Add XP to leveling system
      this.levelingSystem.addXP(event.experienceAwarded, this.playerInventory);

      // Generate and spawn loot
      const loot = LootTable.generateLoot(event.enemyType);
      for (const drop of loot) {
        // Spawn with slight random offset
        const offsetX = (Math.random() - 0.5) * 1.5;
        const offsetY = (Math.random() - 0.5) * 1.5;
        const pos = new Vector3(event.position.x + offsetX, event.position.y + offsetY, 0);
        const droppedItem = new DroppedItem(pos, drop.item, drop.quantity);
        this.droppedItems.push(droppedItem);
      }

      if (loot.length > 0) {
        this.uiManager.showInfoToast(t('notify_loot_dropped'));
      }
    });
  }

  public start(): void {
    if (this.isRunning) return;

    console.log('Starting game loop...');
    this.isRunning = true;
    this.lastTimestamp = performance.now();

    // Show main menu on start
    this.showMainMenu();

    requestAnimationFrame(this.gameLoop.bind(this));
  }

  private showMainMenu(): void {
    this.inMainMenu = true;
    this.isPaused = true;

    // Update main menu with continue availability
    const mainMenuPanel = this.uiManager.getPanel('mainmenu') as MainMenuPanel;
    if (mainMenuPanel) {
      const lastSlot = this.saveManager.getLastPlayedSlot();
      if (lastSlot !== null) {
        const saveInfo = this.saveManager.getSaveInfo(lastSlot);
        mainMenuPanel.setContinueAvailable(true, saveInfo);
      } else {
        mainMenuPanel.setContinueAvailable(false);
      }
      mainMenuPanel.centerOnScreen(this.canvas.width, this.canvas.height);
      mainMenuPanel.show();
    }
  }

  private continueGame(): void {
    const lastSlot = this.saveManager.getLastPlayedSlot();
    if (lastSlot !== null) {
      this.loadFromSlot(lastSlot);
    }
  }

  private startNewGame(): void {
    this.uiManager.hideAll();
    this.restart();
    this.inMainMenu = false;
    this.isPaused = false;
    // Set to next empty save slot so quick save doesn't overwrite existing saves
    const emptySlot = this.saveManager.findEmptySlot();
    this.saveManager.setCurrentSlot(emptySlot >= 0 ? emptySlot : 0);
  }

  private showLoadMenu(): void {
    const mainMenuPanel = this.uiManager.getPanel('mainmenu') as MainMenuPanel;
    mainMenuPanel?.hide();

    const saveLoadPanel = this.uiManager.getPanel('saveload') as SaveLoadPanel;
    if (saveLoadPanel) {
      saveLoadPanel.setMode('load');
      saveLoadPanel.centerOnScreen(this.canvas.width, this.canvas.height);
      saveLoadPanel.show();
    }
  }

  private showSaveMenu(): void {
    // Hide pause menu if visible
    const pauseMenuPanel = this.uiManager.getPanel('pausemenu') as PauseMenuPanel;
    pauseMenuPanel?.hide();

    const saveLoadPanel = this.uiManager.getPanel('saveload') as SaveLoadPanel;
    if (saveLoadPanel) {
      saveLoadPanel.setMode('save');
      saveLoadPanel.centerOnScreen(this.canvas.width, this.canvas.height);
      saveLoadPanel.show();
    }
  }

  private saveToSlot(slot: number, name: string): void {
    const saveData = this.getSaveData();
    const success = this.saveManager.save(saveData, slot, name);
    if (success) {
      this.uiManager.showSuccessToast(t('notify_saved'));
    }
    // Resume game after saving
    this.resumeGame();
  }

  private loadFromSlot(slot: number): void {
    if (this.load(slot)) {
      this.uiManager.hideAll();
      this.inMainMenu = false;
      this.isPaused = false;
      this.isGameOver = false;
      this.uiManager.showSuccessToast(t('notify_loaded'));
    }
  }

  private deleteSave(slot: number): void {
    this.saveManager.deleteSave(slot);
    // Refresh the panel
    const saveLoadPanel = this.uiManager.getPanel('saveload') as SaveLoadPanel;
    saveLoadPanel?.refreshSaves();
  }

  private showPauseMenu(): void {
    this.isPaused = true;
    const pauseMenuPanel = this.uiManager.getPanel('pausemenu') as PauseMenuPanel;
    if (pauseMenuPanel) {
      pauseMenuPanel.centerOnScreen(this.canvas.width, this.canvas.height);
      pauseMenuPanel.show();
    }
  }

  private resumeGame(): void {
    this.isPaused = false;
    this.uiManager.hideAll();
  }

  private quickSave(): void {
    const currentSlot = this.saveManager.getCurrentSlot();
    const existingInfo = this.saveManager.getSaveInfo(currentSlot);
    const name = existingInfo.exists ? existingInfo.name : `Save ${currentSlot + 1}`;
    this.saveToSlot(currentSlot, name);
  }

  private showLoadMenuFromPause(): void {
    const pauseMenuPanel = this.uiManager.getPanel('pausemenu') as PauseMenuPanel;
    pauseMenuPanel?.hide();

    const saveLoadPanel = this.uiManager.getPanel('saveload') as SaveLoadPanel;
    if (saveLoadPanel) {
      saveLoadPanel.setMode('load');
      saveLoadPanel.centerOnScreen(this.canvas.width, this.canvas.height);
      saveLoadPanel.show();
    }
  }

  public stop(): void {
    this.isRunning = false;
  }

  private gameLoop(timestamp: number): void {
    if (!this.isRunning) return;

    // Calculate delta time
    this.deltaTime = (timestamp - this.lastTimestamp) / 1000;
    this.lastTimestamp = timestamp;

    // Cap delta time
    if (this.deltaTime > 0.1) {
      this.deltaTime = 0.1;
    }

    // Update FPS
    this.updateFPS(timestamp);

    // Handle input
    this.handleInput();

    // Update world transition animation (even when paused)
    this.updateTransition(this.deltaTime);

    // Update game if not paused
    if (!this.isPaused) {
      this.update(this.deltaTime);
    }

    // Render
    this.render();

    // Clear per-frame input state
    this.input.update();

    // Process events
    EventBus.processQueue();

    // Continue loop
    requestAnimationFrame(this.gameLoop.bind(this));
  }

  private handleInput(): void {
    // Initialize audio on first click
    if (!this.audioInitialized && this.input.isMouseButtonPressed(MOUSE_LEFT)) {
      AudioManager.init();
      this.audioInitialized = true;
    }

    // Handle game over state - check for restart or return to menu
    if (this.isGameOver) {
      if (this.input.isKeyPressed('r') || this.input.isMouseButtonPressed(MOUSE_LEFT)) {
        this.showMainMenu();
        this.isGameOver = false;
      }
      return; // Don't process other input when game over
    }

    // Check for keyboard input for save rename
    const saveLoadPanel = this.uiManager.getPanel('saveload') as SaveLoadPanel;
    if (saveLoadPanel?.getIsVisible() && saveLoadPanel.isEditingName()) {
      // Handle all key presses for rename (use text input keys to preserve case)
      const textKeys = this.input.getTextInputKeys();
      for (const key of textKeys) {
        if (saveLoadPanel.handleKeyDown(key)) {
          return;
        }
      }
    }

    // UI input first
    const mousePos = this.input.getMousePosition();
    const hud = this.uiManager.getHUD();

    // Handle HUD and panel drag and drop
    if (this.input.isMouseButtonPressed(MOUSE_LEFT)) {
      // Check if clicking on HUD hotbar first
      if (hud.handleMouseDown(mousePos)) {
        // Started potential drag on HUD - don't process other click actions
        return;
      } else {
        // Try to start drag on panel (for inventory/storage drag and drop)
        this.uiManager.handleMouseDown(mousePos);
        // Also handle click for button presses
        if (this.uiManager.handleClick(mousePos)) {
          return; // UI consumed the click
        }
      }
    }

    // Update HUD mouse move for dragging
    hud.handleMouseMove(mousePos);

    // Handle mouse release for HUD and panel drag drop
    if (this.input.isMouseButtonReleased(MOUSE_LEFT)) {
      if (hud.handleMouseUp(mousePos)) {
        return; // HUD handled the drop
      }
      if (this.uiManager.handleMouseUp(mousePos)) {
        return; // Panel handled the drop
      }
    }

    // If HUD is dragging, don't process other mouse input
    if (hud.isDraggingItem()) {
      this.uiManager.handleMouseMove(mousePos);
      return;
    }

    this.uiManager.handleMouseMove(mousePos);

    // Check for interactive structure under mouse cursor for tooltip
    this.updateStructureHoverTooltip(mousePos, hud);

    // Update enemy hover detection for visual feedback
    this.updateEnemyHover(mousePos);

    // Handle scroll for UI panels
    const wheelDeltaUI = this.input.getMouseWheelDelta();
    if (wheelDeltaUI !== 0) {
      if (saveLoadPanel?.getIsVisible()) {
        saveLoadPanel.handleScroll(wheelDeltaUI * 30);
      }
      const craftingPanel = this.uiManager.getPanel('crafting') as CraftingPanel;
      if (craftingPanel?.getIsVisible()) {
        craftingPanel.handleScroll(wheelDeltaUI * 30);
      }
    }

    // In main menu, don't process game input
    if (this.inMainMenu) {
      // Escape in save/load panel goes back to main menu
      if (this.input.isKeyPressed('escape')) {
        if (saveLoadPanel?.getIsVisible()) {
          saveLoadPanel.hide();
          const mainMenuPanel = this.uiManager.getPanel('mainmenu') as MainMenuPanel;
          mainMenuPanel?.show();
        }
      }
      return;
    }

    // Handle escape key for pause menu and panels
    if (this.input.isKeyPressed('escape')) {
      const pauseMenuPanel = this.uiManager.getPanel('pausemenu') as PauseMenuPanel;

      if (saveLoadPanel?.getIsVisible()) {
        // In save/load panel from pause menu - go back to pause menu
        saveLoadPanel.hide();
        pauseMenuPanel?.show();
      } else if (pauseMenuPanel?.getIsVisible()) {
        // In pause menu - resume game
        this.resumeGame();
      } else if (this.uiManager.hasVisiblePanel()) {
        // Other panels open - close them
        this.uiManager.hideAll();
        this.buildingSystem.exitBuildMode();
      } else {
        // No panels open - show pause menu
        this.showPauseMenu();
      }
      return;
    }

    // UI toggles - work even when other panels are open (but not when paused)
    if (!this.isPaused) {
      if (this.input.isKeyPressed('i')) {
        // Check if inventory is currently visible to toggle off
        const inventoryPanel = this.uiManager.getPanel('inventory');
        const wasVisible = inventoryPanel?.getIsVisible();

        this.uiManager.hideAll();
        this.buildingSystem.exitBuildMode();

        // Only show panels if they weren't already visible
        if (!wasVisible) {
          this.uiManager.showPanel('inventory');
          this.uiManager.showPanel('equipment');
        }
        return;
      }
      if (this.input.isKeyPressed('c')) {
        this.uiManager.hideAll();
        this.buildingSystem.exitBuildMode();
        // Reset to basic crafting (no station)
        const craftingPanel = this.uiManager.getPanel('crafting') as CraftingPanel;
        if (craftingPanel) {
          craftingPanel.setCurrentStation(null);
        }
        this.uiManager.togglePanel('crafting');
        return;
      }
      if (this.input.isKeyPressed('b')) {
        this.uiManager.hideAll();
        if (this.buildingSystem.isInBuildMode()) {
          this.buildingSystem.exitBuildMode();
        } else {
          this.uiManager.togglePanel('build');
        }
        return;
      }
    }

    // If paused or UI open, don't process other game input
    if (this.isPaused || this.uiManager.hasVisiblePanel()) {
      return;
    }

    // Quick save with F5, quick load with F9
    if (this.input.isKeyPressed('f5')) {
      this.quickSave();
      return;
    }
    if (this.input.isKeyPressed('f9')) {
      this.loadFromSlot(this.saveManager.getCurrentSlot());
      return;
    }

    // Apply equipment bonuses to player stats
    const equipBonuses = this.playerEquipment.getTotalBonuses();
    const baseSpeed = 6;
    const baseHealth = 100;
    const baseStamina = 100;
    const modifiedSpeed = baseSpeed * (1 + (equipBonuses.moveSpeed || 0));
    const modifiedMaxHealth = baseHealth + (equipBonuses.maxHealth || 0);
    const modifiedMaxStamina = baseStamina + (equipBonuses.maxStamina || 0);
    this.player.movement.setSpeed(modifiedSpeed);
    this.player.health.setMaxHealth(modifiedMaxHealth);
    this.player.stamina.setMaxStamina(modifiedMaxStamina);

    // Player movement
    const moveDir = this.input.getMovementVector();
    this.player.move(moveDir);

    // Flying (hold space if ability unlocked)
    if (this.levelingSystem.hasAbility('fly')) {
      this.player.setCanFly(true);
      this.player.setFlying(this.input.isKeyDown(' '));
    }

    // Hotbar selection
    for (let i = 0; i < 10; i++) {
      const key = ((i + 1) % 10).toString();
      if (this.input.isKeyPressed(key)) {
        this.playerInventory.setSelectedSlot(i);
      }
    }

    // Mouse wheel for hotbar
    const wheelDelta = this.input.getMouseWheelDelta();
    if (wheelDelta > 0) {
      this.playerInventory.selectNext();
    } else if (wheelDelta < 0) {
      this.playerInventory.selectPrevious();
    }

    // Use selected item (E key)
    if (this.input.isKeyPressed('e')) {
      this.useSelectedItem();
    }

    // Manual reload (R key)
    if (this.input.isKeyPressed('r')) {
      this.manualReload();
    }

    // Zoom
    if (this.input.isKeyDown('=') || this.input.isKeyDown('+')) {
      this.renderer.setZoom(this.renderer.getZoom() + this.deltaTime);
    }
    if (this.input.isKeyDown('-')) {
      this.renderer.setZoom(this.renderer.getZoom() - this.deltaTime);
    }

    // Building mode
    if (this.buildingSystem.isInBuildMode()) {
      const worldPos = this.renderer.screenToWorld(mousePos);
      this.buildingSystem.updatePreview(worldPos, this.world);

      if (this.input.isMouseButtonPressed(MOUSE_LEFT)) {
        const result = this.buildingSystem.place(this.world, this.playerInventory);
        if (result.success) {
          console.log(result.message);
        }
      }
      return;
    }

    // Single-click actions: Interact with structures, gather resources, use tools (requires each click)
    // Don't interact if mouse is over the hotbar
    if (this.input.isMouseButtonPressed(MOUSE_LEFT) && hud.getSlotAtPosition(mousePos) < 0) {
      if (this.handleWorldInteraction(mousePos)) {
        return; // Handled an interaction or gathering
      }
    }

    // Continuous actions: Combat (can hold mouse button while moving with WASD)
    // Don't attack if mouse is over the hotbar
    if (this.input.isMouseButtonDown(MOUSE_LEFT) && hud.getSlotAtPosition(mousePos) < 0) {
      this.handleCombat(mousePos);
    }
  }

  /** Handle structure interactions, tool usage, and resource gathering (single click actions) */
  private handleWorldInteraction(screenPos: Vector2): boolean {
    const worldPos = this.renderer.screenToWorld(screenPos);
    const gatherRange = 3; // How far the player can reach for resources/structures

    const selectedItem = this.playerInventory.getSelectedItem();

    // Find the tile that was actually clicked (accounting for tile height)
    const clickedTile = this.findClickedTile(screenPos, worldPos);

    // Check if clicking on a structure (chest, workbench) using clicked tile position
    if (clickedTile) {
      const tilePos = new Vector3(clickedTile.x, clickedTile.y, 0);
      const structure = this.buildingSystem.getStructureAt(tilePos);

      if (structure && structure.isInteractable()) {
        const structCenter = new Vector3(structure.position.x + 0.5, structure.position.y + 0.5, 0);
        const distToPlayer = this.player.position.horizontalDistance(structCenter);

        if (distToPlayer <= gatherRange) {
          // Open storage panel for chests
          if (structure.blueprint.structureType === StructureType.Storage && structure.storage) {
            const storagePanel = this.uiManager.getPanel('storage') as StoragePanel;
            if (storagePanel) {
              storagePanel.setStorageInventory(structure.storage);
              this.uiManager.togglePanel('storage');
            }
            return true;
          }
          // Open crafting panel at workbench for advanced recipes
          if (structure.blueprint.structureType === StructureType.CraftingStation) {
            const craftingPanel = this.uiManager.getPanel('crafting') as CraftingPanel;
            if (craftingPanel) {
              craftingPanel.setCurrentStation('workbench');
              this.uiManager.showPanel('crafting');
              this.uiManager.showInfoToast(t('notify_workbench'));
            }
            return true;
          }
          // Open campfire panel for furnace structures
          if (structure.blueprint.structureType === StructureType.Furnace) {
            const campfirePanel = this.uiManager.getPanel('campfire') as CampfirePanel;
            if (campfirePanel) {
              campfirePanel.setCampfire(structure);
              campfirePanel.setInventory(this.playerInventory);
              this.uiManager.showPanel('campfire');
            }
            return true;
          }
          // Teleport to Hell when clicking on Hell Hole
          if (structure.blueprint.structureType === StructureType.HellHole) {
            this.startWorldTransition('hell');
            return true;
          }
          // Teleport back to overworld when clicking on Ladder
          if (structure.blueprint.structureType === StructureType.Ladder) {
            this.startWorldTransition('overworld');
            return true;
          }
          console.log(`Interacted with ${structure.blueprint.name}`);
          return true;
        }
      }
    }

    // Check if using Jackdrill on the ground to create Hell Hole
    if (clickedTile && selectedItem?.item instanceof ToolItem && selectedItem.item.toolType === 'drill') {
      const tileCenter = new Vector3(clickedTile.x + 0.5, clickedTile.y + 0.5, 0);
      const distToPlayer = this.player.position.horizontalDistance(tileCenter);

      if (distToPlayer <= gatherRange) {
        // Can only drill in overworld
        if (this.worldManager.getCurrentWorldId() !== 'overworld') {
          this.uiManager.showInfoToast('Cannot drill deeper in Hell!');
          return true;
        }

        // Check if there's already a structure here
        const existingStructure = this.buildingSystem.getStructureAt(new Vector3(clickedTile.x, clickedTile.y, 0));
        if (existingStructure) {
          this.uiManager.showInfoToast('Cannot drill here!');
          return true;
        }

        // Check if tile is drillable (not water, not resource)
        if (clickedTile.type === TileType.Water || clickedTile.hasResource()) {
          this.uiManager.showInfoToast('Cannot drill here!');
          return true;
        }

        // Use the drill (consume durability)
        if (selectedItem.item.use()) {
          // Create Hell Hole structure
          const holePosition = new Vector3(clickedTile.x, clickedTile.y, 0);
          this.buildingSystem.restoreStructure('hell_hole', holePosition, 9999, this.world);
          this.uiManager.showSuccessToast('You drilled a portal to Hell!');
          console.log('Hell Hole created at', clickedTile.x, clickedTile.y);

          // Check if drill broke
          if (selectedItem.item.isBroken()) {
            this.uiManager.showInfoToast('Your Jackdrill broke!');
          }
        }
        return true;
      }
    }

    // Check if clicked tile has a resource and is in range
    if (clickedTile && clickedTile.hasResource()) {
      const tileCenter = new Vector3(clickedTile.x + 0.5, clickedTile.y + 0.5, 0);
      const distToPlayer = this.player.position.horizontalDistance(tileCenter);

      if (distToPlayer <= gatherRange) {
        this.gatherResource(clickedTile);
        return true;
      }
    }

    return false; // Nothing was interacted with
  }

  /** Handle combat - attacking enemies (continuous action, can hold mouse button) */
  private handleCombat(screenPos: Vector2): void {
    const gatherRange = 3;
    const selectedItem = this.playerInventory.getSelectedItem();

    // Check if we have a ranged weapon selected
    if (selectedItem?.item instanceof RangedWeaponItem) {
      this.handleRangedCombat(screenPos, selectedItem.item);
      return;
    }

    // Calculate attack range based on equipped weapon (swords have longer range)
    let attackRange = gatherRange; // Default range
    if (selectedItem?.item instanceof WeaponItem) {
      // Use weapon's range, but make swords have extra reach
      const weapon = selectedItem.item;
      attackRange = weapon.range + 1.5; // Base bonus for all weapons
      if (weapon.weaponType === 'sword') {
        attackRange += 1; // Extra bonus for swords
      } else if (weapon.weaponType === 'spear') {
        attackRange += 1.5; // Spears have the longest reach
      }
    }

    // Check if clicking on an enemy (using screen-space hit detection)
    const enemies = this.enemySpawner.getEnemies();
    for (const enemy of enemies) {
      // Convert enemy position to screen coordinates
      const enemyScreenPos = this.renderer.worldToScreen(enemy.position);

      // Calculate sprite bounds in screen space
      // Sprite is centered on enemyScreenPos with size = config.size * 2 * zoom
      const spriteSize = enemy.config.size * 2 * this.renderer.getZoom();
      const halfSize = spriteSize / 2;

      // Check if click is within sprite bounds (generous hitbox)
      const dx = Math.abs(screenPos.x - enemyScreenPos.x);
      const dy = Math.abs(screenPos.y - enemyScreenPos.y);

      // Use rectangular hitbox that's slightly larger for better feel
      const hitboxWidth = halfSize * 1.2;
      const hitboxHeight = spriteSize * 0.8; // Account for sprite being taller

      const clickedOnEnemy = dx < hitboxWidth && dy < hitboxHeight;
      const distToPlayer = enemy.position.horizontalDistance(this.player.position);

      if (clickedOnEnemy && distToPlayer <= attackRange) {
        // Clicked on enemy within range - attack it
        if (this.player.attack()) {
          enemy.health.takeDamage(this.player.getAttackDamage());
          console.log(`Hit ${enemy.enemyType} for ${this.player.getAttackDamage()} damage!`);
          if (enemy.health.isDead()) {
            EventBus.publish({
              type: 'ENEMY_KILLED',
              enemyType: enemy.enemyType,
              position: { x: enemy.position.x, y: enemy.position.y },
              experienceAwarded: enemy.config.experienceValue
            });
          }
        }
        return;
      }
    }

    // General attack in clicked direction
    if (this.player.attack()) {
      console.log('Attack swing');
    }
  }

  /** Handle ranged weapon firing */
  private handleRangedCombat(screenPos: Vector2, weapon: RangedWeaponItem): void {
    // Check fire rate cooldown
    const now = performance.now();
    const fireInterval = 1000 / weapon.fireRate; // ms between shots
    if (now - this.lastFireTime < fireInterval) {
      return; // Still on cooldown
    }

    // Check if reloading
    if (weapon.getIsReloading()) {
      return;
    }

    // Check if weapon has ammo in magazine
    if (weapon.getCurrentAmmo() <= 0) {
      // Auto-reload if possible
      const ammoStack = this.findAmmoInInventory(weapon.ammoType);
      if (ammoStack) {
        weapon.startReload();
        this.uiManager.showInfoToast(`Reloading ${weapon.name}...`);
      } else {
        this.uiManager.showInfoToast(`No ${weapon.ammoType} ammo!`);
      }
      return;
    }

    // Fire the weapon
    if (!weapon.fire()) {
      return;
    }

    this.lastFireTime = now;

    // Get world position of where we're shooting
    const worldPos = this.renderer.screenToWorld(screenPos);

    // Calculate direction from player to target (in world space)
    const rawDirection = new Vector2(
      worldPos.x - this.player.position.x,
      worldPos.y - this.player.position.y
    );

    // Normalize direction before applying spread
    const direction = rawDirection.magnitude() > 0.01
      ? rawDirection.normalize()
      : new Vector2(1, 0);

    // Apply spread based on accuracy
    const spreadAngle = weapon.getSpreadAngle();
    const cos = Math.cos(spreadAngle);
    const sin = Math.sin(spreadAngle);
    const spreadDirection = new Vector2(
      direction.x * cos - direction.y * sin,
      direction.x * sin + direction.y * cos
    );

    console.log('Firing:', {
      screenClick: { x: screenPos.x, y: screenPos.y },
      worldTarget: { x: worldPos.x, y: worldPos.y },
      playerPos: { x: this.player.position.x, y: this.player.position.y },
      direction: { x: direction.x, y: direction.y },
      spreadDir: { x: spreadDirection.x, y: spreadDirection.y }
    });

    // Create projectile - start slightly in front of player in firing direction
    const startOffset = 0.5; // Start projectile slightly ahead of player
    const startPos = new Vector3(
      this.player.position.x + spreadDirection.x * startOffset,
      this.player.position.y + spreadDirection.y * startOffset,
      this.player.position.z
    );

    const projectile = new Projectile(
      startPos,
      spreadDirection,
      {
        type: weapon.projectileType,
        damage: weapon.damage,
        speed: weapon.projectileSpeed,
        range: weapon.range,
        color: weapon.projectileColor,
        size: weapon.projectileType === 'rail' ? 6 : weapon.projectileType === 'plasma' ? 5 : 4, // Increased size for visibility
        piercing: weapon.projectileType === 'rail',
        explosive: weapon.projectileType === 'plasma',
        explosionRadius: weapon.projectileType === 'plasma' ? 2 : 0,
        trailLength: weapon.projectileType === 'laser' || weapon.projectileType === 'plasma' || weapon.projectileType === 'rail' ? 10 : 5
      },
      'player'
    );

    // Immediately update the projectile to give it a head start
    // This makes the firing effect appear instantly rather than waiting for the next frame
    projectile.update(0.033); // ~2 frames worth of movement for instant visual feedback

    this.projectileManager.add(projectile);
    console.log(`Fired ${weapon.name}! Ammo: ${weapon.getCurrentAmmo()}/${weapon.magazineSize}`);
  }

  /** Find ammo in inventory */
  private findAmmoInInventory(ammoType: string): { index: number; quantity: number } | null {
    for (let i = 0; i < this.playerInventory.getCapacity(); i++) {
      const slot = this.playerInventory.getSlot(i);
      if (slot && slot.item.id === ammoType && slot.quantity > 0) {
        return { index: i, quantity: slot.quantity };
      }
    }
    return null;
  }

  /** Update ranged weapon reload */
  private updateRangedWeaponReload(deltaTime: number): void {
    const selectedItem = this.playerInventory.getSelectedItem();
    if (!(selectedItem?.item instanceof RangedWeaponItem)) return;

    const weapon = selectedItem.item;
    if (!weapon.getIsReloading()) return;

    // Find ammo in inventory
    const ammoStack = this.findAmmoInInventory(weapon.ammoType);
    const availableAmmo = ammoStack?.quantity ?? 0;

    // Update reload progress
    const ammoConsumed = weapon.updateReload(deltaTime, availableAmmo);

    // Remove consumed ammo from inventory
    if (ammoConsumed > 0 && ammoStack) {
      this.playerInventory.removeItemAtSlot(ammoStack.index, ammoConsumed);
      this.uiManager.showSuccessToast(`Reloaded! ${weapon.getCurrentAmmo()}/${weapon.magazineSize}`);
    }
  }

  /** Render aiming line when holding a ranged weapon */
  private renderAimingLine(): void {
    const selectedItem = this.playerInventory.getSelectedItem();
    if (!(selectedItem?.item instanceof RangedWeaponItem)) return;

    const weapon = selectedItem.item;
    const mousePos = this.input.getMousePosition();
    const ctx = this.renderer.getContext();

    // Get player screen position
    const playerScreenPos = this.renderer.worldToScreen(this.player.position);

    // Get target world position and convert to screen
    const targetWorldPos = this.renderer.screenToWorld(mousePos);
    const targetScreenPos = this.renderer.worldToScreen(targetWorldPos);

    // Calculate direction for the line
    const dx = targetScreenPos.x - playerScreenPos.x;
    const dy = targetScreenPos.y - playerScreenPos.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist < 10) return; // Too close, don't render

    // Normalize direction
    const dirX = dx / dist;
    const dirY = dy / dist;

    // Calculate line length based on weapon range
    const maxRange = weapon.range * 32 * this.renderer.getZoom(); // Approximate screen distance
    const lineLength = Math.min(dist, maxRange);

    // Start position (slightly in front of player)
    const startOffset = 15;
    const startX = playerScreenPos.x + dirX * startOffset;
    const startY = playerScreenPos.y + dirY * startOffset;

    // End position
    const endX = playerScreenPos.x + dirX * lineLength;
    const endY = playerScreenPos.y + dirY * lineLength;

    ctx.save();

    // Choose color based on weapon type
    let lineColor = 'rgba(255, 0, 0, 0.3)'; // Default red
    let dotColor = 'rgba(255, 0, 0, 0.8)';

    if (weapon.projectileType === 'laser') {
      lineColor = 'rgba(255, 0, 0, 0.4)';
      dotColor = 'rgba(255, 0, 0, 1)';
    } else if (weapon.projectileType === 'plasma') {
      lineColor = 'rgba(255, 0, 255, 0.3)';
      dotColor = 'rgba(255, 0, 255, 0.8)';
    } else if (weapon.projectileType === 'rail') {
      lineColor = 'rgba(0, 255, 255, 0.4)';
      dotColor = 'rgba(0, 255, 255, 1)';
    } else {
      // Bullets - subtle white line
      lineColor = 'rgba(255, 255, 255, 0.15)';
      dotColor = 'rgba(255, 255, 255, 0.6)';
    }

    // Draw aiming line (dashed for non-energy weapons)
    ctx.strokeStyle = lineColor;
    ctx.lineWidth = weapon.projectileType === 'laser' || weapon.projectileType === 'rail' ? 2 : 1;

    if (weapon.projectileType === 'bullet' || weapon.projectileType === 'shell') {
      ctx.setLineDash([5, 10]);
    }

    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.lineTo(endX, endY);
    ctx.stroke();

    ctx.setLineDash([]);

    // Draw targeting reticle at end
    const reticleSize = 8;

    // Outer circle
    ctx.strokeStyle = dotColor;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(endX, endY, reticleSize, 0, Math.PI * 2);
    ctx.stroke();

    // Inner dot
    ctx.fillStyle = dotColor;
    ctx.beginPath();
    ctx.arc(endX, endY, 2, 0, Math.PI * 2);
    ctx.fill();

    // Crosshairs
    ctx.beginPath();
    ctx.moveTo(endX - reticleSize - 3, endY);
    ctx.lineTo(endX - reticleSize + 3, endY);
    ctx.moveTo(endX + reticleSize - 3, endY);
    ctx.lineTo(endX + reticleSize + 3, endY);
    ctx.moveTo(endX, endY - reticleSize - 3);
    ctx.lineTo(endX, endY - reticleSize + 3);
    ctx.moveTo(endX, endY + reticleSize - 3);
    ctx.lineTo(endX, endY + reticleSize + 3);
    ctx.stroke();

    // Show "OUT OF RANGE" if target is beyond weapon range
    if (dist > maxRange) {
      ctx.fillStyle = 'rgba(255, 100, 100, 0.8)';
      ctx.font = 'bold 10px monospace';
      ctx.textAlign = 'center';
      ctx.fillText('OUT OF RANGE', endX, endY - 15);
    }

    // Show "RELOADING" at reticle if reloading
    if (weapon.getIsReloading()) {
      ctx.fillStyle = 'rgba(255, 200, 0, 0.9)';
      ctx.font = 'bold 10px monospace';
      ctx.textAlign = 'center';
      ctx.fillText('RELOADING', endX, endY - 15);
    } else if (weapon.getCurrentAmmo() === 0) {
      ctx.fillStyle = 'rgba(255, 100, 100, 0.9)';
      ctx.font = 'bold 10px monospace';
      ctx.textAlign = 'center';
      ctx.fillText('NO AMMO', endX, endY - 15);
    }

    ctx.restore();
  }

  /** Manual reload triggered by R key */
  private manualReload(): void {
    const selectedItem = this.playerInventory.getSelectedItem();
    if (!(selectedItem?.item instanceof RangedWeaponItem)) {
      return;
    }

    const weapon = selectedItem.item;

    // Already reloading or full
    if (weapon.getIsReloading()) {
      return;
    }
    if (weapon.getCurrentAmmo() >= weapon.getMagazineSize()) {
      this.uiManager.showInfoToast('Magazine full!');
      return;
    }

    // Check for ammo
    const ammoStack = this.findAmmoInInventory(weapon.ammoType);
    if (!ammoStack) {
      this.uiManager.showInfoToast(`No ${weapon.ammoType} ammo!`);
      return;
    }

    weapon.startReload();
    this.uiManager.showInfoToast(`Reloading ${weapon.name}...`);
  }

  /** Update the HUD with ranged weapon info */
  private updateRangedWeaponHUD(): void {
    const selectedItem = this.playerInventory.getSelectedItem();

    if (selectedItem?.item instanceof RangedWeaponItem) {
      const weapon = selectedItem.item;

      // Count total ammo in inventory
      let totalAmmoInInventory = 0;
      for (let i = 0; i < this.playerInventory.getCapacity(); i++) {
        const slot = this.playerInventory.getSlot(i);
        if (slot && slot.item.id === weapon.ammoType) {
          totalAmmoInInventory += slot.quantity;
        }
      }

      this.uiManager.getHUD().setRangedWeaponInfo({
        currentAmmo: weapon.getCurrentAmmo(),
        magazineSize: weapon.getMagazineSize(),
        isReloading: weapon.getIsReloading(),
        reloadProgress: weapon.getReloadProgress(),
        weaponName: weapon.name,
        ammoInInventory: totalAmmoInInventory
      });
    } else {
      // No ranged weapon selected, hide the display
      this.uiManager.getHUD().setRangedWeaponInfo(null);
    }
  }

  /** Update projectiles and check for enemy collisions */
  private updateProjectiles(deltaTime: number): void {
    // Update projectile positions
    this.projectileManager.update(deltaTime);

    // Check for collisions with enemies
    const projectiles = this.projectileManager.getActiveProjectiles();
    const enemies = this.enemySpawner.getEnemies();

    for (const projectile of projectiles) {
      if (!projectile.isActive || projectile.isPendingDestroy()) continue;

      for (const enemy of enemies) {
        if (enemy.health.isDead()) continue;

        // Check collision (use enemy size as radius)
        const enemyRadius = enemy.config.size * 0.5;
        if (projectile.checkCollision(enemy.position, enemyRadius)) {
          // Hit!
          enemy.health.takeDamage(projectile.config.damage);
          console.log(`Projectile hit ${enemy.enemyType} for ${projectile.config.damage} damage!`);

          // Handle piercing projectiles (railgun goes through enemies)
          if (!projectile.config.piercing) {
            projectile.destroy();
          }

          // Handle explosive projectiles (plasma deals AoE)
          if (projectile.config.explosive && projectile.config.explosionRadius) {
            this.handleExplosion(projectile.position, projectile.config.explosionRadius, projectile.config.damage * 0.5);
          }

          // Check for kill
          if (enemy.health.isDead()) {
            EventBus.publish({
              type: 'ENEMY_KILLED',
              enemyType: enemy.enemyType,
              position: { x: enemy.position.x, y: enemy.position.y },
              experienceAwarded: enemy.config.experienceValue
            });
          }

          // Break if projectile is destroyed (non-piercing)
          if (!projectile.isActive) break;
        }
      }
    }
  }

  /** Handle explosive projectile AoE damage */
  private handleExplosion(position: Vector3, radius: number, damage: number): void {
    const enemies = this.enemySpawner.getEnemies();

    for (const enemy of enemies) {
      if (enemy.health.isDead()) continue;

      const dist = enemy.position.horizontalDistance(position);
      if (dist <= radius) {
        // Apply damage falloff based on distance
        const falloff = 1 - (dist / radius);
        const explosionDamage = damage * falloff;
        enemy.health.takeDamage(explosionDamage);
        console.log(`Explosion hit ${enemy.enemyType} for ${explosionDamage.toFixed(1)} damage!`);

        // Check for kill
        if (enemy.health.isDead()) {
          EventBus.publish({
            type: 'ENEMY_KILLED',
            enemyType: enemy.enemyType,
            position: { x: enemy.position.x, y: enemy.position.y },
            experienceAwarded: enemy.config.experienceValue
          });
        }
      }
    }
  }

  private gatherResource(tile: { x: number; y: number; type: TileType; resourceType: ResourceType | null; harvest: (amount: number) => number }): void {
    if (!tile.resourceType) return;

    // Save resource type before harvesting (harvest may clear it)
    const resourceType = tile.resourceType;
    const tileType = tile.type;

    // Check for appropriate tool
    const selectedItem = this.playerInventory.getSelectedItem();
    let gatherPower = 1;

    if (selectedItem?.item instanceof ToolItem) {
      const tool = selectedItem.item;
      if (tileType === TileType.Tree && tool.toolType === 'axe') {
        gatherPower = tool.toolPower;
        tool.use();
      } else if (tileType === TileType.Rock && tool.toolType === 'pickaxe') {
        gatherPower = tool.toolPower;
        tool.use();
      } else if (tileType === TileType.IronOre && tool.toolType === 'pickaxe') {
        gatherPower = tool.toolPower;
        tool.use();
      } else if (tileType === TileType.Bush && tool.toolType === 'sickle') {
        gatherPower = tool.toolPower;
        tool.use();
      }
    }

    // Iron ore requires a pickaxe to mine
    if (tileType === TileType.IronOre && gatherPower === 1) {
      console.log('Iron ore requires a pickaxe to mine!');
      return;
    }

    const gathered = tile.harvest(gatherPower);
    if (gathered > 0) {
      // Map resource type to item ID
      let itemId: string;
      switch (resourceType) {
        case ResourceType.Wood:
          itemId = 'wood';
          break;
        case ResourceType.Stone:
          itemId = 'stone';
          break;
        case ResourceType.Fiber:
          itemId = 'fiber';
          break;
        case ResourceType.Iron:
          itemId = 'iron_ore';
          break;
        default:
          itemId = 'wood'; // Fallback
      }
      const item = ItemRegistry.get(itemId);
      if (item) {
        this.playerInventory.addItem(item, gathered);
        EventBus.publish({
          type: 'RESOURCE_GATHERED',
          resourceType: resourceType,
          amount: gathered
        });
        console.log(`Gathered ${gathered} ${itemId}`);
      }

      // Chance to drop food when gathering wood (from trees)
      if (resourceType === ResourceType.Wood) {
        const foodDropChance = 0.08; // 8% chance per gather
        if (Math.random() < foodDropChance) {
          const apple = ItemRegistry.get('apple');
          if (apple) {
            this.playerInventory.addItem(apple, 1);
            console.log('Found an apple!');
          }
        }
      }
    }
  }

  /** Use the selected item (eat food, etc.) */
  private useSelectedItem(): void {
    const selected = this.playerInventory.getSelectedItem();
    if (!selected) return;

    const item = selected.item;

    // Handle torch toggle
    if (item instanceof TorchItem) {
      if (item.isBurntOut()) {
        this.uiManager.showInfoToast(t('torch_burnt_out'));
      } else {
        item.toggle();
        if (item.getIsLit()) {
          this.uiManager.showSuccessToast(t('torch_lit'));
        } else {
          this.uiManager.showInfoToast(t('torch_extinguished'));
        }
      }
      return;
    }

    // Handle consumable items
    if (item instanceof ConsumableItem) {
      const currentHealth = this.player.health.getCurrentHealth();
      const maxHealth = this.player.health.getMaxHealth();
      const currentStamina = this.player.stamina.getCurrentStamina();
      const maxStamina = this.player.stamina.getMaxStamina();

      // Check if we need healing or stamina
      const needsHealth = currentHealth < maxHealth && item.healthRestore > 0;
      const needsStamina = currentStamina < maxStamina && item.staminaRestore > 0;

      if (needsHealth || needsStamina) {
        // Apply effects
        if (item.healthRestore > 0) {
          this.player.health.heal(item.healthRestore);
        }
        if (item.staminaRestore > 0) {
          this.player.stamina.restore(item.staminaRestore);
        }

        // Remove one item from inventory
        this.playerInventory.removeItemAtSlot(this.playerInventory.getSelectedSlot(), 1);

        console.log(`Used ${item.name}: +${item.healthRestore} HP, +${item.staminaRestore} Stamina`);
      } else {
        console.log('Already at full health and stamina!');
      }
    }
  }

  /** Update torch durability when lit */
  private updateTorch(deltaTime: number): void {
    // Check all inventory slots for lit torches
    for (let i = 0; i < this.playerInventory.getCapacity(); i++) {
      const slot = this.playerInventory.getSlot(i);
      if (slot?.item instanceof TorchItem) {
        const torch = slot.item;
        if (torch.getIsLit()) {
          torch.update(deltaTime);
          // Notify when torch burns out
          if (torch.isBurntOut()) {
            this.uiManager.showInfoToast(t('torch_burnt_out'));
          }
        }
      }
    }
  }

  private updateDroppedItems(deltaTime: number): void {
    // Update all dropped items
    for (let i = this.droppedItems.length - 1; i >= 0; i--) {
      const droppedItem = this.droppedItems[i];
      if (!droppedItem) continue;

      droppedItem.update(deltaTime);

      // Remove inactive items
      if (!droppedItem.isActive) {
        this.droppedItems.splice(i, 1);
        continue;
      }

      // Check for auto-pickup
      if (droppedItem.canPickup() && droppedItem.isInRange(this.player.position)) {
        const overflow = this.playerInventory.addItem(droppedItem.item, droppedItem.quantity);
        const pickedUp = droppedItem.quantity - overflow;
        if (pickedUp > 0) {
          const itemInfo = Localization.getItem(droppedItem.item.id);
          const itemName = itemInfo?.name || droppedItem.item.name;
          this.uiManager.showSuccessToast(t('notify_picked_up', pickedUp, itemName));
          droppedItem.isActive = false;
          this.droppedItems.splice(i, 1);
        }
      }
    }
  }

  private update(deltaTime: number): void {
    // Update time
    this.timeManager.update(deltaTime);
    this.uiManager.getHUD().setTime(
      this.timeManager.getTimeFormatted(),
      this.timeManager.getDay()
    );

    // Update level display
    this.uiManager.getHUD().setLevel(
      this.levelingSystem.getLevel(),
      this.levelingSystem.getXP(),
      this.levelingSystem.getLevelProgress(),
      this.levelingSystem.isMaxLevel()
    );

    // Update ranged weapon HUD
    this.updateRangedWeaponHUD();

    // Update camera
    this.camera.update(deltaTime);
    this.renderer.setCameraOffset(this.camera.getOffset());

    // Update world and entities (pass dayLength for tree regrowth calculation)
    this.world.update(deltaTime, this.timeManager.getDayLength());

    // Update enemy spawner
    this.enemySpawner.update(deltaTime);

    // Update building system
    this.buildingSystem.update(deltaTime);

    // Update dropped items and check for pickup
    this.updateDroppedItems(deltaTime);

    // Update projectiles and check for hits
    this.updateProjectiles(deltaTime);

    // Update ranged weapon reload
    this.updateRangedWeaponReload(deltaTime);

    // Update torch durability if lit
    this.updateTorch(deltaTime);

    // Update UI
    this.uiManager.update(deltaTime);

    // Auto-save
    this.saveManager.update(deltaTime, () => this.getSaveData());

    // Update cursor type based on what's being hovered
    this.updateCursor();
  }

  private updateCursor(): void {
    const mousePos = this.input.getMousePosition();

    // Check if hovering over a UI panel
    if (this.uiManager.isOverPanel(mousePos)) {
      this.cursor.setCursor('hand');
      return;
    }

    // Check if hovering over an interactable structure within range
    const worldPos = this.renderer.screenToWorld(mousePos);
    const clickedTile = this.findClickedTile(mousePos, worldPos);
    if (clickedTile) {
      const tilePos = new Vector3(clickedTile.x, clickedTile.y, 0);
      const structure = this.buildingSystem.getStructureAt(tilePos);
      if (structure && structure.isInteractable()) {
        const structCenter = new Vector3(structure.position.x + 0.5, structure.position.y + 0.5, 0);
        const distToPlayer = this.player.position.horizontalDistance(structCenter);
        if (distToPlayer <= 3) {
          this.cursor.setCursor('hand');
          return;
        }
      }
    }

    // Default to pointer cursor
    this.cursor.setCursor('pointer');
  }

  private render(): void {
    // Clear
    this.renderer.clear();

    // Render world, entities, and structures together (depth sorted)
    this.world.render(this.renderer, this.player.position, 20, this.buildingSystem);

    // Render subtle highlight on hovered enemy
    if (this.hoveredEnemy) {
      this.renderEnemyHighlight(this.hoveredEnemy);
    }

    // Render subtle highlight on hovered interactive structure
    if (this.hoveredStructure) {
      this.renderStructureHighlight(this.hoveredStructure);
    }

    // Render dropped items
    for (const item of this.droppedItems) {
      item.render(this.renderer);
    }

    // Render aiming line for ranged weapons
    this.renderAimingLine();

    // Render build preview
    this.buildingSystem.renderPreview(this.renderer);

    // Collect light sources from burning campfires
    const lightSources: { worldPos: Vector3; radius: number; color?: string }[] = [];
    for (const structure of this.buildingSystem.getAllStructures()) {
      if (structure.campfire?.isBurning) {
        lightSources.push({
          worldPos: new Vector3(structure.position.x + 0.5, structure.position.y + 0.5, 0),
          radius: structure.getLightRadius()
        });
      }
    }

    // Add player torch light if holding a lit torch
    const selectedItem = this.playerInventory.getSelectedItem();
    if (selectedItem?.item instanceof TorchItem && selectedItem.item.getIsLit()) {
      lightSources.push({
        worldPos: this.player.position,
        radius: selectedItem.item.getLightRadius(),
        color: selectedItem.item.getLightColor()
      });
    }

    // Add hellfire glow if holding the Demon Blade
    if (selectedItem?.item instanceof WeaponItem && selectedItem.item.id === 'demon_blade') {
      lightSources.push({
        worldPos: this.player.position,
        radius: 4, // Medium light radius
        color: 'rgba(255, 80, 30, 0.2)' // Hellfire orange-red glow
      });
    }

    // Apply day/night lighting with campfire light sources
    this.renderer.applyDarknessOverlay(this.timeManager.getDaylightIntensity(), lightSources);

    // Render projectiles AFTER darkness overlay so they're visible in dark areas
    this.projectileManager.render(this.renderer);

    // Render overlays BEFORE UI so panels appear on top
    // Main menu overlay (darker background)
    if (this.inMainMenu) {
      this.renderMainMenuOverlay();
    }

    // Pause overlay (only when not in main menu and not transitioning)
    if (this.isPaused && !this.isGameOver && !this.inMainMenu && !this.transitionState?.active) {
      this.renderPauseOverlay();
    }

    // World transition overlay (on top of everything except UI)
    this.renderTransitionOverlay();

    // Render UI (on top of overlays)
    this.uiManager.render(this.renderer.getContext(), this.canvas.width, this.canvas.height);

    // Render debug info
    this.renderDebugInfo();

    // Render game over screen (on top of everything)
    if (this.isGameOver) {
      this.renderGameOverScreen();
    }

    // Render custom cursor (always on top)
    const mousePos = this.input.getMousePosition();
    this.cursor.render(this.renderer.getContext(), mousePos.x, mousePos.y);
  }

  private renderMainMenuOverlay(): void {
    const ctx = this.renderer.getContext();
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
  }

  /** Render a subtle highlight around the hovered enemy */
  private renderEnemyHighlight(enemy: Enemy): void {
    const ctx = this.renderer.getContext();
    const screenPos = this.renderer.worldToScreen(enemy.position);
    const zoom = this.renderer.getZoom();
    const baseSize = enemy.config.size * zoom;

    // Check if enemy is within attack range
    const distToEnemy = this.player.position.horizontalDistance(enemy.position);
    const attackRange = this.getPlayerAttackRange();
    const inRange = distToEnemy <= attackRange;

    ctx.save();

    // Different colors based on whether enemy is in attack range
    // Green/teal when in range (can attack), red when out of range
    const strokeColor = inRange ? 'rgba(78, 204, 163, 0.7)' : 'rgba(255, 100, 100, 0.5)';
    const fillColorInner = inRange ? 'rgba(78, 204, 163, 0.25)' : 'rgba(255, 50, 50, 0.15)';
    const fillColorOuter = inRange ? 'rgba(78, 204, 163, 0)' : 'rgba(255, 50, 50, 0)';

    // Ellipse size - slightly larger when in range
    const sizeMultiplier = inRange ? 1.4 : 1.2;
    const ellipseWidth = baseSize * sizeMultiplier;
    const ellipseHeight = baseSize * (sizeMultiplier / 2); // Flattened for isometric view

    // Outer ring
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = (inRange ? 3 : 2) * zoom;
    ctx.beginPath();
    ctx.ellipse(screenPos.x, screenPos.y + baseSize * 0.3, ellipseWidth, ellipseHeight, 0, 0, Math.PI * 2);
    ctx.stroke();

    // Inner fill
    const gradient = ctx.createRadialGradient(
      screenPos.x, screenPos.y + baseSize * 0.3, 0,
      screenPos.x, screenPos.y + baseSize * 0.3, ellipseWidth
    );
    gradient.addColorStop(0, fillColorInner);
    gradient.addColorStop(1, fillColorOuter);

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.ellipse(screenPos.x, screenPos.y + baseSize * 0.3, ellipseWidth, ellipseHeight, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  /** Get the player's current attack range based on equipped weapon */
  private getPlayerAttackRange(): number {
    const gatherRange = 3; // Base range
    let attackRange = gatherRange;

    const selectedItem = this.playerInventory.getSelectedItem();
    if (selectedItem?.item instanceof WeaponItem) {
      const weapon = selectedItem.item;
      attackRange = weapon.range + 1.5; // Base bonus for all weapons
      if (weapon.weaponType === 'sword') {
        attackRange += 1; // Extra bonus for swords
      } else if (weapon.weaponType === 'spear') {
        attackRange += 1.5; // Spears have the longest reach
      }
    }

    return attackRange;
  }

  /** Render a subtle highlight around a hovered interactive structure */
  private renderStructureHighlight(structure: Structure): void {
    const ctx = this.renderer.getContext();
    const zoom = this.renderer.getZoom();

    // Get structure shadow position (matching Structure.render logic)
    const shadowPos = this.renderer.worldToScreen(
      new Vector3(structure.position.x, structure.position.y, 0)
    );

    ctx.save();

    // Subtle ellipse glow under the structure (like a selection indicator)
    // Match the shadow size from Structure.render (20 * zoom width, 10 * zoom height)
    const ellipseWidth = 24 * zoom;
    const ellipseHeight = 12 * zoom; // Flattened for isometric view

    // Position slightly below shadow center (matching Structure shadow offset of +5)
    const highlightY = shadowPos.y + 5;

    // Outer subtle ring - cyan/teal color for interactive structures
    ctx.strokeStyle = 'rgba(78, 204, 163, 0.7)';
    ctx.lineWidth = 2 * zoom;
    ctx.beginPath();
    ctx.ellipse(shadowPos.x, highlightY, ellipseWidth, ellipseHeight, 0, 0, Math.PI * 2);
    ctx.stroke();

    // Inner fill (very subtle)
    const gradient = ctx.createRadialGradient(
      shadowPos.x, highlightY, 0,
      shadowPos.x, highlightY, ellipseWidth
    );
    gradient.addColorStop(0, 'rgba(78, 204, 163, 0.2)');
    gradient.addColorStop(1, 'rgba(78, 204, 163, 0)');

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.ellipse(shadowPos.x, highlightY, ellipseWidth, ellipseHeight, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  private renderDebugInfo(): void {
    const ctx = this.renderer.getContext();
    ctx.fillStyle = '#888';
    ctx.font = '12px monospace';
    ctx.textAlign = 'left';
    ctx.fillText(`FPS: ${this.fps}`, 10, this.canvas.height - 50);
    ctx.fillText(`Enemies: ${this.enemySpawner.getEnemyCount()}`, 10, this.canvas.height - 35);
    ctx.fillText(`Pos: ${this.player.position.x.toFixed(1)}, ${this.player.position.y.toFixed(1)}`, 10, this.canvas.height - 20);
  }

  private renderPauseOverlay(): void {
    const ctx = this.renderer.getContext();
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    // The PauseMenuPanel renders the actual menu UI
  }

  private renderGameOverScreen(): void {
    const ctx = this.renderer.getContext();

    // Dark overlay
    ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Game Over text
    ctx.fillStyle = '#e74c3c';
    ctx.font = 'bold 64px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(t('gameover_title'), this.canvas.width / 2, this.canvas.height / 2 - 40);

    // Stats
    ctx.fillStyle = '#aaa';
    ctx.font = '18px monospace';
    ctx.fillText(t('gameover_survived', this.timeManager.getDay()), this.canvas.width / 2, this.canvas.height / 2 + 20);

    // Restart prompt
    ctx.fillStyle = '#4ecca3';
    ctx.font = 'bold 24px monospace';
    ctx.fillText(t('gameover_restart'), this.canvas.width / 2, this.canvas.height / 2 + 80);
  }

  private updateFPS(timestamp: number): void {
    this.frameCount++;
    if (timestamp - this.fpsUpdateTime >= 1000) {
      this.fps = this.frameCount;
      this.frameCount = 0;
      this.fpsUpdateTime = timestamp;
    }
  }

  private getSaveData(): SaveData {
    // Helper to save structures from a building system
    const saveStructures = (buildingSystem: BuildingSystem) => {
      return buildingSystem.getAllStructures().map(s => {
        const structData: SaveData['structures'][0] = {
          id: s.id,
          blueprintId: s.blueprint.id,
          position: { x: s.position.x, y: s.position.y, z: s.position.z },
          health: s.health
        };

        if (s.campfire) {
          structData.campfire = {
            fuel: s.campfire.fuel,
            isBurning: s.campfire.isBurning,
            cookingItem: s.campfire.cookingItem,
            rawQuantity: s.campfire.rawQuantity,
            cookingProgress: s.campfire.cookingProgress,
            cookedQuantity: s.campfire.cookedQuantity
          };
        }

        if (s.storage) {
          const storageData = s.storage.toSaveData() as { slots: ({ itemId: string; quantity: number } | null)[] };
          structData.storage = { slots: storageData.slots };
        }

        return structData;
      });
    };

    // Save all worlds from WorldManager
    const worldIds = this.worldManager.getWorldIds();
    const worlds: WorldSaveData[] = worldIds.map(worldId => {
      const worldData = this.worldManager.getWorldData(worldId);
      if (!worldData) {
        throw new Error(`World data not found for ${worldId}`);
      }
      return {
        worldId,
        worldType: worldData.world.worldType,
        seed: worldData.world.getSeed(),
        modifiedTiles: worldData.world.getModifiedTiles().map(t => ({
          x: t.x,
          y: t.y,
          type: t.type,
          resourceAmount: t.resourceAmount,
          regrowthStage: t.regrowthStage,
          regrowthTimer: t.regrowthTimer,
          wasTree: t.wasTree,
          wasBush: t.wasBush
        })),
        playerLastPosition: {
          x: worldData.playerLastPosition.x,
          y: worldData.playerLastPosition.y,
          z: worldData.playerLastPosition.z
        },
        structures: saveStructures(worldData.buildingSystem)
      };
    });

    // Save current world position for player
    this.worldManager.savePlayerPosition(this.player.position);

    return {
      version: 1,
      timestamp: Date.now(),
      name: '', // Will be set by save() method
      player: {
        position: {
          x: this.player.position.x,
          y: this.player.position.y,
          z: this.player.position.z
        },
        health: this.player.health.getCurrentHealth(),
        maxHealth: this.player.health.getMaxHealth(),
        stamina: this.player.stamina.getCurrentStamina(),
        maxStamina: this.player.stamina.getMaxStamina(),
        ...this.levelingSystem.toSaveData()
      },
      inventory: this.playerInventory.toSaveData() as SaveData['inventory'],
      equipment: this.playerEquipment.toSaveData() as SaveData['equipment'],
      time: {
        gameTime: this.timeManager.getTime(),
        day: this.timeManager.getDay(),
        totalPlayTime: this.timeManager.getTotalPlayTime()
      },
      // Legacy single-world data (for backwards compatibility when loading)
      world: {
        seed: this.worldManager.getBaseSeed(),
        modifiedTiles: this.world.getModifiedTiles().map(t => ({
          x: t.x,
          y: t.y,
          type: t.type,
          resourceAmount: t.resourceAmount,
          regrowthStage: t.regrowthStage,
          regrowthTimer: t.regrowthTimer,
          wasTree: t.wasTree,
          wasBush: t.wasBush
        }))
      },
      structures: saveStructures(this.buildingSystem),
      // Multi-world data
      currentWorldId: this.worldManager.getCurrentWorldId(),
      worlds
    };
  }

  public save(slot?: number): boolean {
    return this.saveManager.save(this.getSaveData(), slot);
  }

  public load(slot: number): boolean {
    const data = this.saveManager.load(slot);
    if (!data) return false;

    // Clear existing worlds
    this.worldManager.clearAllWorlds();

    // Check for multi-world save data
    if (data.worlds && data.worlds.length > 0) {
      // Restore all worlds from multi-world save
      this.worldManager.setBaseSeed(data.world.seed);

      for (const worldSaveData of data.worlds) {
        const worldData = this.worldManager.restoreWorld(
          worldSaveData.worldId,
          worldSaveData.seed,
          worldSaveData.worldType,
          worldSaveData.playerLastPosition
        );

        // Apply modified tiles
        if (worldSaveData.modifiedTiles) {
          worldData.world.applyModifiedTiles(worldSaveData.modifiedTiles.map(t => ({
            x: t.x,
            y: t.y,
            type: t.type as TileType,
            resourceAmount: t.resourceAmount,
            regrowthStage: t.regrowthStage,
            regrowthTimer: t.regrowthTimer,
            wasTree: t.wasTree,
            wasBush: t.wasBush
          })));
        }

        // Restore structures for this world
        worldData.buildingSystem.clearStructures();
        for (const structData of worldSaveData.structures) {
          const pos = new Vector3(structData.position.x, structData.position.y, structData.position.z);
          worldData.buildingSystem.restoreStructure(
            structData.blueprintId,
            pos,
            structData.health,
            worldData.world,
            structData.campfire,
            structData.storage
          );
        }
      }

      // Set current world
      const currentWorldId = data.currentWorldId || 'overworld';
      const currentWorldData = this.worldManager.getOrCreateWorld(currentWorldId);
      this.world = currentWorldData.world;
      this.buildingSystem = currentWorldData.buildingSystem;
      this.enemySpawner = currentWorldData.enemySpawner as EnemySpawner;

    } else {
      // Legacy single-world restore
      this.worldManager.setBaseSeed(data.world.seed);
      const overworldData = this.worldManager.initializeOverworld();
      this.world = overworldData.world;
      this.buildingSystem = overworldData.buildingSystem;
      this.enemySpawner = overworldData.enemySpawner as EnemySpawner;

      // Apply modified tiles
      if (data.world.modifiedTiles) {
        this.world.applyModifiedTiles(data.world.modifiedTiles.map(t => ({
          x: t.x,
          y: t.y,
          type: t.type as TileType,
          resourceAmount: t.resourceAmount,
          regrowthStage: t.regrowthStage,
          regrowthTimer: t.regrowthTimer,
          wasTree: t.wasTree,
          wasBush: t.wasBush
        })));
      }

      // Restore structures
      this.buildingSystem.clearStructures();
      for (const structData of data.structures) {
        const pos = new Vector3(structData.position.x, structData.position.y, structData.position.z);
        this.buildingSystem.restoreStructure(
          structData.blueprintId,
          pos,
          structData.health,
          this.world,
          structData.campfire,
          structData.storage
        );
      }
    }

    // Restore player position
    const playerPos = new Vector3(
      data.player.position.x,
      data.player.position.y,
      data.player.position.z
    );

    // Create new player at saved position
    this.player = new Player(playerPos);
    this.player.movement.canMoveTo = (x, y) => this.canPlayerMoveTo(x, y);
    this.world.addEntity(this.player);

    // Setup enemy spawner
    this.enemySpawner.setPlayer(this.player);
    this.enemySpawner.enemyCanMoveTo = (x, y) => this.canEnemyMoveTo(x, y);

    // Restore player stats
    this.player.health.setHealth(data.player.health);
    this.player.stamina.restore(data.player.stamina);

    // Restore level/XP
    if (data.player.level !== undefined && data.player.xp !== undefined) {
      this.levelingSystem.fromSaveData({
        level: data.player.level,
        xp: data.player.xp,
        abilities: data.player.abilities
      });
      // Sync flying ability with player
      if (this.levelingSystem.hasAbility('fly')) {
        this.player.setCanFly(true);
      }
    }

    // Restore inventory
    this.playerInventory = new InventoryComponent(20);
    this.player.addComponent(this.playerInventory);
    this.player.setInventoryRef(this.playerInventory);
    this.playerInventory.fromSaveData(data.inventory);

    // Restore equipment
    this.playerEquipment = new EquipmentComponent();
    if (data.equipment) {
      this.playerEquipment.fromSaveData(
        data.equipment.map(e => ({ slot: e.slot as import('./items/Item').EquipmentSlot, itemId: e.itemId })),
        { get: (id: string) => ItemRegistry.get(id) as import('./items/Item').ArmorItem | null }
      );
    }

    // Update UI references
    const inventoryPanel = this.uiManager.getPanel('inventory') as InventoryPanel;
    if (inventoryPanel) inventoryPanel.setInventory(this.playerInventory);

    const craftingPanel = this.uiManager.getPanel('crafting') as CraftingPanel;
    if (craftingPanel) craftingPanel.setInventory(this.playerInventory);

    const buildPanel = this.uiManager.getPanel('build') as BuildPanel;
    if (buildPanel) {
      buildPanel.setInventory(this.playerInventory);
      buildPanel.setBuildingSystem(this.buildingSystem);
    }

    const storagePanel = this.uiManager.getPanel('storage') as StoragePanel;
    if (storagePanel) storagePanel.setPlayerInventory(this.playerInventory);

    const campfirePanel = this.uiManager.getPanel('campfire') as CampfirePanel;
    if (campfirePanel) campfirePanel.setInventory(this.playerInventory);

    const equipmentPanel = this.uiManager.getPanel('equipment') as EquipmentPanel;
    if (equipmentPanel) {
      equipmentPanel.setEquipment(this.playerEquipment);
      equipmentPanel.setInventory(this.playerInventory);
    }

    // Update HUD
    this.uiManager.getHUD().setPlayer(this.player);
    this.uiManager.getHUD().setInventory(this.playerInventory);

    // Restore time
    this.timeManager.setState(
      data.time.gameTime,
      data.time.day,
      data.time.totalPlayTime
    );

    // Camera follows player
    this.camera.follow(this.player);

    console.log('Game loaded from slot', slot);
    return true;
  }

  public destroy(): void {
    this.stop();
    this.input.destroy();
    AudioManager.destroy();
    EventBus.clear();
    console.log('Game destroyed');
  }

  private restart(): void {
    console.log('Restarting game...');

    // Reset game state flags
    this.isGameOver = false;
    this.isPaused = false;

    // Reset WorldManager with new seed
    this.worldManager.clearAllWorlds();
    this.worldManager = new WorldManager(Date.now());
    const overworldData = this.worldManager.initializeOverworld();
    this.world = overworldData.world;
    this.buildingSystem = overworldData.buildingSystem;
    this.enemySpawner = overworldData.enemySpawner as EnemySpawner;

    // Find a new spawn point
    const spawnPos = this.findSpawnPoint();

    // Reset player
    this.player = new Player(spawnPos);
    this.player.movement.canMoveTo = (x, y) => this.canPlayerMoveTo(x, y);
    this.world.addEntity(this.player);

    // Setup enemy spawner
    this.enemySpawner.setPlayer(this.player);
    this.enemySpawner.enemyCanMoveTo = (x, y) => this.canEnemyMoveTo(x, y);

    // Reset inventory
    this.playerInventory = new InventoryComponent(20);
    this.player.addComponent(this.playerInventory);
    this.player.setInventoryRef(this.playerInventory);

    // Reset equipment (player starts naked)
    this.playerEquipment = new EquipmentComponent();

    // Give starting items again
    this.giveStartingItems();

    // Reset leveling system
    this.levelingSystem = new LevelingSystem();
    this.levelingSystem.onLevelUp = (level, reward) => {
      this.uiManager.showSuccessToast(`Level ${level}! ${reward.description}`);
      console.log(`Leveled up to ${level}!`, reward);
    };
    this.levelingSystem.onXPGained = (amount, _total) => {
      this.uiManager.showInfoToast(`+${amount} XP`);
    };

    // Update UI with new references
    const inventoryPanel = this.uiManager.getPanel('inventory') as InventoryPanel;
    if (inventoryPanel) {
      inventoryPanel.setInventory(this.playerInventory);
    }

    const craftingPanel = this.uiManager.getPanel('crafting') as CraftingPanel;
    if (craftingPanel) {
      craftingPanel.setInventory(this.playerInventory);
    }

    const buildPanel = this.uiManager.getPanel('build') as BuildPanel;
    if (buildPanel) {
      buildPanel.setInventory(this.playerInventory);
      buildPanel.setBuildingSystem(this.buildingSystem);
    }

    const campfirePanel = this.uiManager.getPanel('campfire') as CampfirePanel;
    if (campfirePanel) {
      campfirePanel.setInventory(this.playerInventory);
    }

    const storagePanel = this.uiManager.getPanel('storage') as StoragePanel;
    if (storagePanel) {
      storagePanel.setPlayerInventory(this.playerInventory);
    }

    const equipmentPanel = this.uiManager.getPanel('equipment') as EquipmentPanel;
    if (equipmentPanel) {
      equipmentPanel.setEquipment(this.playerEquipment);
      equipmentPanel.setInventory(this.playerInventory);
    }

    // Reset HUD
    this.uiManager.getHUD().setPlayer(this.player);
    this.uiManager.getHUD().setInventory(this.playerInventory);

    // Reset time
    this.timeManager = new TimeManager(300);
    this.setupTimeEvents();

    // Exit build mode
    this.buildingSystem.exitBuildMode();

    // Clear dropped items from old game
    this.droppedItems = [];

    // Clear projectiles
    this.projectileManager.clear();

    // Camera follows new player
    this.camera.follow(this.player);

    // Hide all UI panels
    this.uiManager.hideAll();

    console.log('Game restarted!');
  }

  /** Find a walkable spawn point near origin */
  private findSpawnPoint(): Vector3 {
    // Search in expanding squares from origin
    for (let radius = 0; radius < 20; radius++) {
      for (let dx = -radius; dx <= radius; dx++) {
        for (let dy = -radius; dy <= radius; dy++) {
          if (Math.abs(dx) !== radius && Math.abs(dy) !== radius) continue;

          if (this.world.isWalkable(dx, dy)) {
            console.log(`Found spawn point at (${dx}, ${dy})`);
            return new Vector3(dx, dy, 0);
          }
        }
      }
    }
    // Fallback - should never happen
    console.warn('No walkable spawn point found, using origin');
    return new Vector3(0, 0, 0);
  }

  /** Update structure hover tooltip based on mouse position */
  private updateStructureHoverTooltip(mousePos: Vector2, hud: import('./ui/HUD').HUD): void {
    // Find structure by checking visual sprite bounds
    const structure = this.findStructureAtScreenPos(mousePos);

    if (!structure || !structure.isInteractable()) {
      hud.setHoveredStructure(null);
      this.hoveredStructure = null;
      return;
    }

    // Check if player is close enough to interact
    const structCenter = new Vector3(structure.position.x + 0.5, structure.position.y + 0.5, 0);
    const distToPlayer = this.player.position.horizontalDistance(structCenter);
    const interactRange = 3;

    if (distToPlayer > interactRange) {
      hud.setHoveredStructure(null);
      this.hoveredStructure = null;
      return;
    }

    // Set the hovered structure for visual highlight
    this.hoveredStructure = structure;

    // Get structure screen position for tooltip
    const structScreenPos = this.renderer.worldToScreen(structCenter);

    // Get localized name and hint based on structure type
    const structInfo = Localization.getStructure(structure.blueprint.id);
    const name = structInfo?.name || structure.blueprint.name;

    let hint = '';
    if (structure.blueprint.structureType === StructureType.Furnace) {
      hint = t('hint_campfire');
    } else if (structure.blueprint.structureType === StructureType.Storage) {
      hint = t('hint_chest');
    } else if (structure.blueprint.structureType === StructureType.CraftingStation) {
      hint = t('hint_workbench');
    }

    const hoverInfo: StructureHoverInfo = {
      name,
      hint,
      screenX: structScreenPos.x,
      screenY: structScreenPos.y
    };

    hud.setHoveredStructure(hoverInfo);
  }

  /** Update which enemy is being hovered for visual feedback */
  private updateEnemyHover(screenPos: Vector2): void {
    this.hoveredEnemy = null;

    const enemies = this.enemySpawner.getEnemies();
    for (const enemy of enemies) {
      // Convert enemy position to screen coordinates
      const enemyScreenPos = this.renderer.worldToScreen(enemy.position);

      // Calculate sprite bounds in screen space (same as click detection)
      const spriteSize = enemy.config.size * 2 * this.renderer.getZoom();
      const halfSize = spriteSize / 2;

      // Check if mouse is within sprite bounds
      const dx = Math.abs(screenPos.x - enemyScreenPos.x);
      const dy = Math.abs(screenPos.y - enemyScreenPos.y);

      const hitboxWidth = halfSize * 1.2;
      const hitboxHeight = spriteSize * 0.8;

      if (dx < hitboxWidth && dy < hitboxHeight) {
        this.hoveredEnemy = enemy;
        return;
      }
    }
  }

  /** Find a structure at the given screen position by checking visual sprite bounds */
  private findStructureAtScreenPos(screenPos: Vector2): Structure | null {
    const zoom = this.renderer.getZoom();
    const spriteSize = 50 * zoom;
    const halfSize = spriteSize / 2;

    interface StructureCandidate {
      structure: Structure;
      depth: number;
    }

    const candidates: StructureCandidate[] = [];

    for (const structure of this.buildingSystem.getAllStructures()) {
      // Skip non-interactive structures like floors and walls
      if (!structure.isInteractable()) continue;

      // Calculate where the sprite is rendered (matching Structure.render logic)
      const structType = structure.blueprint.structureType;
      const spriteHeight = structType === StructureType.Furnace ? 6 : 12;
      const structScreenPos = this.renderer.worldToScreen(
        new Vector3(structure.position.x, structure.position.y, spriteHeight)
      );

      // Check if mouse is within sprite bounds
      const minX = structScreenPos.x - halfSize;
      const maxX = structScreenPos.x + halfSize;
      const minY = structScreenPos.y - halfSize;
      const maxY = structScreenPos.y + halfSize;

      if (screenPos.x >= minX && screenPos.x <= maxX &&
          screenPos.y >= minY && screenPos.y <= maxY) {
        // Calculate depth for sorting (structures in front should be checked first)
        const depth = structure.position.x + structure.position.y;
        candidates.push({ structure, depth });
      }
    }

    // Sort by depth (higher depth = in front in isometric view)
    candidates.sort((a, b) => b.depth - a.depth);

    return candidates.length > 0 ? candidates[0]!.structure : null;
  }

  /** Find which tile was clicked, accounting for tile heights */
  private findClickedTile(screenPos: Vector2, groundWorldPos: Vector3) {
    // Check tiles from front to back (higher depth first) to find which one was clicked
    // This accounts for tall tiles that may overlap with tiles behind them
    const searchRadius = 3;
    const baseX = Math.floor(groundWorldPos.x);
    const baseY = Math.floor(groundWorldPos.y);

    type TileType = ReturnType<typeof this.world.getTile>;
    interface TileCandidate {
      tile: NonNullable<TileType>;
      depth: number;
    }

    const candidates: TileCandidate[] = [];

    // Check nearby tiles
    for (let dy = -searchRadius; dy <= searchRadius; dy++) {
      for (let dx = -searchRadius; dx <= searchRadius; dx++) {
        const tx = baseX + dx;
        const ty = baseY + dy;
        const tile = this.world.getTile(tx, ty);

        if (tile) {
          const height = tile.getHeight();
          const tileWorldPos = new Vector3(tx, ty, 0);

          // Get screen bounds of this tile
          const tileScreenPos = this.renderer.worldToScreen(tileWorldPos);
          const tileTopScreenPos = this.renderer.worldToScreen(new Vector3(tx, ty, height));

          // Check if click is within tile's diamond shape (simplified as bounding box)
          const tileHalfWidth = 32 * this.renderer.getZoom(); // TILE_WIDTH / 2
          const tileHalfHeight = 16 * this.renderer.getZoom(); // TILE_HEIGHT / 2

          const minX = tileScreenPos.x - tileHalfWidth;
          const maxX = tileScreenPos.x + tileHalfWidth;
          const minY = tileTopScreenPos.y - tileHalfHeight;
          const maxY = tileScreenPos.y + tileHalfHeight;

          if (screenPos.x >= minX && screenPos.x <= maxX &&
              screenPos.y >= minY && screenPos.y <= maxY) {
            candidates.push({
              tile,
              depth: tx + ty + height / 10 // Higher tiles and tiles in front have higher depth
            });
          }
        }
      }
    }

    // Sort by depth (highest first - tiles in front)
    candidates.sort((a, b) => b.depth - a.depth);

    // Return the frontmost tile that was clicked
    return candidates.length > 0 ? candidates[0]!.tile : this.world.getTile(baseX, baseY);
  }

  /** Start a world transition animation */
  private startWorldTransition(targetWorld: string): void {
    // Don't start if already transitioning
    if (this.transitionState?.active) return;

    // Don't transition to same world
    if (this.worldManager.getCurrentWorldId() === targetWorld) return;

    this.transitionState = {
      active: true,
      phase: 'falling',
      progress: 0,
      targetWorld
    };

    // Pause game during transition
    this.isPaused = true;

    console.log(`Starting transition to ${targetWorld}`);
  }

  /** Update the world transition animation */
  private updateTransition(deltaTime: number): void {
    if (!this.transitionState?.active) return;

    const FALL_DURATION = 1.0;  // Seconds for fall animation
    const BLACK_DURATION = 0.5; // Seconds of black screen
    const RISE_DURATION = 1.0;  // Seconds for fade in

    this.transitionState.progress += deltaTime;

    switch (this.transitionState.phase) {
      case 'falling':
        if (this.transitionState.progress >= FALL_DURATION) {
          // Switch to black phase and actually teleport
          this.transitionState.phase = 'black';
          this.transitionState.progress = 0;
          this.performWorldSwitch(this.transitionState.targetWorld);
        }
        break;

      case 'black':
        if (this.transitionState.progress >= BLACK_DURATION) {
          // Switch to rising phase
          this.transitionState.phase = 'rising';
          this.transitionState.progress = 0;
        }
        break;

      case 'rising':
        if (this.transitionState.progress >= RISE_DURATION) {
          // Transition complete
          this.transitionState = null;
          this.isPaused = false;
          console.log('Transition complete');
        }
        break;
    }
  }

  /** Actually switch to a different world */
  private performWorldSwitch(targetWorld: string): void {
    // Switch world in manager - this returns the saved position if available
    const savedPosition = this.worldManager.switchWorld(targetWorld, this.player);

    // Update local references to current world's systems
    const worldData = this.worldManager.getCurrentWorldData();
    this.world = worldData.world;
    this.buildingSystem = worldData.buildingSystem;
    this.enemySpawner = worldData.enemySpawner as EnemySpawner;

    // Check if hell has been set up by looking for existing structures
    // (can't use position check since spawn could be at 0,0)
    const hellHasStructures = targetWorld === 'hell' && this.buildingSystem.getAllStructures().length > 0;

    // Use saved position for return visits, find new spawn only for first visit
    let newPosition: Vector3;
    if (hellHasStructures) {
      // Return visit - use saved position
      newPosition = savedPosition;
    } else {
      // First visit or new world - find spawn point
      newPosition = this.findSpawnPoint();
      // Save this position for future visits
      this.worldManager.savePlayerPosition(newPosition);
    }

    // Update player position
    this.player.position.x = newPosition.x;
    this.player.position.y = newPosition.y;
    this.player.position.z = newPosition.z;

    // Add player to new world's entity list (for rendering)
    this.world.addEntity(this.player);

    // Setup enemy spawner callbacks
    this.enemySpawner.setPlayer(this.player);
    this.enemySpawner.enemyCanMoveTo = (x, y) => this.canEnemyMoveTo(x, y);

    // Update BuildPanel with new buildingSystem reference
    const buildPanel = this.uiManager.getPanel('build') as BuildPanel;
    if (buildPanel) {
      buildPanel.setBuildingSystem(this.buildingSystem);
    }

    // In Hell, create spawn platform with ladder ONLY on first visit
    if (targetWorld === 'hell' && !hellHasStructures) {
      // Create 10x10 floor platform centered on spawn
      for (let dx = -5; dx <= 4; dx++) {
        for (let dy = -5; dy <= 4; dy++) {
          const floorPos = new Vector3(newPosition.x + dx, newPosition.y + dy, 0);
          this.buildingSystem.restoreStructure('floor_wood', floorPos, 50, this.world);
        }
      }
      console.log('Safe platform created in Hell');

      // Place ladder in the middle of the platform
      const ladderPos = new Vector3(newPosition.x, newPosition.y, 0);
      this.buildingSystem.restoreStructure('ladder', ladderPos, 100, this.world);
      console.log('Ladder placed at hell spawn point');
    }

    // Cleanup: If hell has multiple ladders (from old buggy saves), keep only one
    if (targetWorld === 'hell') {
      const allStructures = this.buildingSystem.getAllStructures();
      const ladders = allStructures.filter(s => s.blueprint.structureType === StructureType.Ladder);

      if (ladders.length > 1) {
        console.log(`Found ${ladders.length} ladders, cleaning up duplicates...`);
        // Keep only the first ladder, remove the rest
        for (let i = 1; i < ladders.length; i++) {
          const ladder = ladders[i]!;
          this.buildingSystem.removeAt(ladder.position, this.world);
        }
        console.log(`Removed ${ladders.length - 1} duplicate ladders`);
      }
    }

    // Update camera to follow player at new position
    this.camera.follow(this.player);

    console.log(`Switched to ${targetWorld} at (${newPosition.x.toFixed(1)}, ${newPosition.y.toFixed(1)})`);
  }

  /** Render the world transition overlay */
  private renderTransitionOverlay(): void {
    if (!this.transitionState?.active) return;

    const ctx = this.renderer.getContext();
    let alpha = 0;

    switch (this.transitionState.phase) {
      case 'falling': {
        // Fade to black with screen shake
        alpha = this.transitionState.progress / 1.0; // 1s duration

        // Screen shake effect
        const shakeAmount = 10 * alpha;
        const shakeX = (Math.random() - 0.5) * shakeAmount;
        const shakeY = (Math.random() - 0.5) * shakeAmount;
        ctx.translate(shakeX, shakeY);
        break;
      }

      case 'black':
        alpha = 1;
        break;

      case 'rising':
        // Fade from black
        alpha = 1 - (this.transitionState.progress / 1.0); // 1s duration
        break;
    }

    ctx.fillStyle = `rgba(0, 0, 0, ${Math.min(1, alpha)})`;
    ctx.fillRect(-100, -100, this.canvas.width + 200, this.canvas.height + 200);

    // Show "Entering Hell..." or "Returning to surface..." text during black phase
    if (this.transitionState.phase === 'black') {
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 32px monospace';
      ctx.textAlign = 'center';
      const text = this.transitionState.targetWorld === 'hell'
        ? 'Entering the Underworld...'
        : 'Returning to surface...';
      ctx.fillText(text, this.canvas.width / 2, this.canvas.height / 2);
    }
  }

  /** Check if player can move to position */
  private canPlayerMoveTo(x: number, y: number): boolean {
    // Flying high bypasses all collision - can fly over everything
    if (this.player.isSafeFromEnemies()) {
      // Still need a tile to exist (world boundary)
      const tile = this.world.getTile(x, y);
      return tile !== null;
    }

    const tile = this.world.getTile(x, y);
    if (!tile) return false;

    // Check all structures at this position (there might be floor + something on top)
    const blocked = this.buildingSystem.isBlockedForPlayer(x, y);
    if (blocked !== null) {
      return !blocked;
    }

    // No structure - check tile walkability
    return tile.walkable;
  }

  /** Check if enemy can move to position */
  public canEnemyMoveTo(x: number, y: number): boolean {
    const tile = this.world.getTile(x, y);
    if (!tile) return false;

    // Check all structures at this position
    const blocked = this.buildingSystem.isBlockedForEnemy(x, y);
    if (blocked !== null) {
      return !blocked;
    }

    // No structure - check tile walkability
    return tile.walkable;
  }
}
